import { useCallback } from 'react';
import type { VcsApi } from '@env0/vcs-service/api';
import { Popup } from 'utils/popup.utils';
import { intl } from 'components/localization';
import { useGetAuthorizationUrl } from 'hooks/use-get-authorization-url';
import type { OAuthResult } from 'components/organizations/vcs/vcs-connection-modal/vcs-connection-modal-form-oauth-authorization-button';
import { useGenerateGitAuthToken } from 'stores/rq/vcs-auth-tokens';
import { useUpdateVcsConnection } from 'stores/rq/vcs-connections';
import useApiClient from 'hooks/use-api-client';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import useStores from 'hooks/use-stores.hooks';
import type { GitProviderTypes } from 'types/api.types';
import { NotificationTypes } from 'types/notifications.types';

type UseVcsOAuthPopupProps = {
  connectionType: VcsApi.ConnectionType;
  vcsType: VcsApi.VcsType;
  authenticationType?: VcsApi.OAuthAuthenticationType;
  accessScope?: string;
  onSuccess: (result: OAuthResult) => void;
  onError?: (error: string) => void;
  onTokenProcessingChange?: (isProcessing: boolean) => void;
};

const TOKEN_BACKED_OAUTH_VCS_TYPES: VcsApi.VcsType[] = ['GitLab', 'AzureDevOps'];

export const useVcsOAuthPopup = ({
  connectionType,
  vcsType,
  authenticationType,
  accessScope,
  onSuccess,
  onError,
  onTokenProcessingChange
}: UseVcsOAuthPopupProps) => {
  const authorizationUrl = useGetAuthorizationUrl({ connectionType, vcsType, authenticationType });
  const { mutateAsync: generateGitAuthToken } = useGenerateGitAuthToken();
  const { mutateAsync: updateVcsConnection } = useUpdateVcsConnection();
  const apiClient = useApiClient();
  const organizationId = useCurrentOrganizationId();
  const { notificationStore } = useStores();

  const openOAuthPopup = useCallback(() => {
    Popup.open({
      url: authorizationUrl,
      closeByMessage: 'authorizationCallback',
      onBlock: () => {
        const errorMsg = intl.formatMessage({ id: 'vcs.authorize-button.blocked' });
        notificationStore.setNotification({
          notificationType: NotificationTypes.error,
          message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
          description: errorMsg
        });
        onError?.(errorMsg);
      },
      onClosed: async result => {
        if (!result?.accessScope) {
          const errorMsg = result?.error || intl.formatMessage({ id: 'vcs.authorize-button.window-closed' });
          notificationStore.setNotification({
            notificationType: NotificationTypes.error,
            message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
          return;
        }
        if (accessScope && accessScope !== result.accessScope) {
          const errorMsg = intl.formatMessage(
            {
              id: `organization.settings.agents.vcs-connections-modal.connection.oauth.accessScopeWrong${
                vcsType === 'AzureDevOps' ? '.AzureDevOps' : ''
              }`
            },
            { accessScope }
          );
          notificationStore.setNotification({
            notificationType: NotificationTypes.error,
            message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
          return;
        }

        let oauthResult: OAuthResult = result as OAuthResult;
        if (TOKEN_BACKED_OAUTH_VCS_TYPES.includes(vcsType) && oauthResult.authorizationId) {
          onTokenProcessingChange?.(true);
          try {
            const token = await generateGitAuthToken({
              gitProvider: vcsType as GitProviderTypes,
              authorizationId: oauthResult.authorizationId
            });
            oauthResult = { ...oauthResult, tokenId: token.id, authorizationId: undefined };
            try {
              const connections = (await apiClient.vcsConnections.listVcsConnections(organizationId)).filter(
                connection => connection.type === vcsType && connection.accessScope === result.accessScope
              );
              // The grant refreshed the token an existing connection points at, so a create would only hit "already exists"
              if (!accessScope && connections.some(connection => connection.connectionType === connectionType)) {
                oauthResult = { ...oauthResult, isAlreadySaved: true };
              }
              if (vcsType === 'AzureDevOps') {
                // The grant just switched the token this user's connections share, and a connection copies
                // its method from the token only on update
                await Promise.all(connections.map(connection => updateVcsConnection({ id: connection.id!, data: {} })));
              }
            } catch {
              // The grant stands, but without the list a create falls back to Save, and Azure DevOps keeps its old stored method
              if (vcsType === 'AzureDevOps') {
                notificationStore.setNotification({
                  notificationType: NotificationTypes.warning,
                  message: intl.formatMessage({ id: 'vcs.authorize-button.sync-failed.title' }),
                  description: intl.formatMessage({ id: 'vcs.authorize-button.sync-failed.description' })
                });
              }
            }
          } catch (error) {
            const errorMsg = (error as Error)?.message ?? 'Failed to create token';
            notificationStore.setNotification({
              notificationType: NotificationTypes.error,
              message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
              description: errorMsg
            });
            onError?.(errorMsg);
            return;
          } finally {
            onTokenProcessingChange?.(false);
          }
        }

        notificationStore.setNotification({
          notificationType: NotificationTypes.success,
          message: intl.formatMessage({ id: 'vcs.authorize-button.success.title' }),
          description: intl.formatMessage({ id: 'vcs.authorize-button.success.description' })
        });
        onSuccess(oauthResult);
      },
      features: { height: 700, width: 700 }
    });
  }, [
    authorizationUrl,
    accessScope,
    connectionType,
    onSuccess,
    onError,
    onTokenProcessingChange,
    vcsType,
    generateGitAuthToken,
    updateVcsConnection,
    apiClient,
    organizationId,
    notificationStore
  ]);

  return { openOAuthPopup };
};
